// src/utils/geoConverter.ts

/**
 * Utilidades de conversión de coordenadas geográficas (WGS84) a
 * coordenadas locales en metros (plano cartesiano) y viceversa.
 * También incluye proyección isométrica para vista 2.5D.
 */

// ----------------------------------------------------------------------
// Tipos y constantes
// ----------------------------------------------------------------------

/** Coordenada geográfica [lat, lng] */
export type LatLng = [number, number];

/** Punto en el plano local (metros) */
export interface LocalPoint {
  x: number;
  z: number; // z positivo hacia el sur si no se invierte
}

/** Punto 3D local */
export interface LocalPoint3D {
  x: number;
  y: number; // altura
  z: number;
}

/** Punto isométrico en pantalla */
export interface IsoPoint {
  x: number;
  y: number; // y en pantalla (hacia abajo)
}

// Radio de la Tierra en metros (WGS84)
const EARTH_RADIUS = 6378137;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

// ----------------------------------------------------------------------
// Clase principal: GeoConverter
// ----------------------------------------------------------------------

/**
 * Convierte coordenadas geográficas a un sistema local cartesiano en metros.
 * El origen se define en un punto (lat, lng) específico.
 */
export class GeoConverter {
  private originLat: number;
  private originLng: number;
  private metersPerDegreeLat: number;
  private metersPerDegreeLng: number;
  private invertZ: boolean; // si true, z positivo apunta al norte

  /**
   * @param originLat - Latitud del origen local
   * @param originLng - Longitud del origen local
   * @param invertZ - Si true, z positivo apunta al norte (por defecto true)
   */
  constructor(originLat: number, originLng: number, invertZ = true) {
    this.originLat = originLat;
    this.originLng = originLng;
    this.invertZ = invertZ;

    // Factores de conversión (aproximación para distancias cortas < 10 km)
    this.metersPerDegreeLat = EARTH_RADIUS * DEG_TO_RAD;
    this.metersPerDegreeLng =
      EARTH_RADIUS * DEG_TO_RAD * Math.cos(originLat * DEG_TO_RAD);
  }

  /** Obtener el origen */
  getOrigin(): { lat: number; lng: number } {
    return { lat: this.originLat, lng: this.originLng };
  }

  /**
   * Convierte lat/lng a coordenadas locales en metros.
   * @param lat - Latitud
   * @param lng - Longitud
   * @returns {x, z} en metros relativos al origen
   */
  latLngToLocal(lat: number, lng: number): LocalPoint {
    const x = (lng - this.originLng) * this.metersPerDegreeLng;
    let z = (lat - this.originLat) * this.metersPerDegreeLat;
    if (this.invertZ) z = -z; // z positivo hacia el norte
    return { x, z };
  }

  /**
   * Convierte coordenadas locales (metros) a lat/lng.
   * @param x - Este-oeste (positivo al este)
   * @param z - Norte-sur (positivo al norte si invertZ es true)
   * @returns [lat, lng]
   */
  localToLatLng(x: number, z: number): LatLng {
    const lng = this.originLng + x / this.metersPerDegreeLng;
    let dz = z;
    if (this.invertZ) dz = -dz;
    const lat = this.originLat + dz / this.metersPerDegreeLat;
    return [lat, lng];
  }

  /**
   * Convierte un array de [lat, lng] a array de puntos locales {x,z}.
   */
  latLngArrayToLocal(coords: LatLng[]): LocalPoint[] {
    return coords.map(([lat, lng]) => this.latLngToLocal(lat, lng));
  }

  /**
   * Convierte un array de puntos locales {x,z} a array de [lat, lng].
   */
  localArrayToLatLng(points: LocalPoint[]): LatLng[] {
    return points.map((p) => this.localToLatLng(p.x, p.z));
  }

  /**
   * Calcula la distancia en metros entre dos puntos geográficos.
   * Usa fórmula haversine.
   */
  distanceBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const dLat = (lat2 - lat1) * DEG_TO_RAD;
    const dLng = (lng2 - lng1) * DEG_TO_RAD;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * DEG_TO_RAD) *
        Math.cos(lat2 * DEG_TO_RAD) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS * c;
  }

  /**
   * Calcula el bounding box geográfico de un área circular.
   * @returns {minLat, maxLat, minLng, maxLng}
   */
  getBoundingBox(radiusMeters: number): {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  } {
    const deltaLat = radiusMeters / this.metersPerDegreeLat;
    const deltaLng = radiusMeters / this.metersPerDegreeLng;
    return {
      minLat: this.originLat - deltaLat,
      maxLat: this.originLat + deltaLat,
      minLng: this.originLng - deltaLng,
      maxLng: this.originLng + deltaLng,
    };
  }
}

// ----------------------------------------------------------------------
// Proyección Isométrica
// ----------------------------------------------------------------------

/**
 * Convierte un punto 3D local (x, y, z) a coordenadas isométricas 2D.
 * La proyección isométrica típica usa ángulos de 30°.
 */
export function worldToIsometric(
  x: number,
  y: number,
  z: number,
  scale = 1
): IsoPoint {
  // Rotación de 45° en el plano XZ y luego inclinación de ~35.264°
  const angle = Math.PI / 4; // 45°
  const tilt = Math.atan(Math.SQRT1_2); // ~35.264°

  const xRot = x * Math.cos(angle) - z * Math.sin(angle);
  const zRot = x * Math.sin(angle) + z * Math.cos(angle);

  const isoX = xRot * scale;
  const isoY = zRot * Math.sin(tilt) * scale - y * scale;

  return { x: isoX, y: isoY };
}

/**
 * Convierte un punto local 2D (x,z) a isométrico asumiendo y=0.
 */
export function localToIsometric(point: LocalPoint, scale = 1): IsoPoint {
  return worldToIsometric(point.x, 0, point.z, scale);
}

/**
 * Crea una matriz de proyección isométrica para Three.js (OrthographicCamera).
 * Devuelve la posición de cámara y el target para lograr vista isométrica.
 */
export function getIsometricCameraSetup(
  distance = 500,
  angleDeg = 45,
  tiltDeg = 35.264
): { position: [number, number, number]; target: [number, number, number] } {
  const angleRad = angleDeg * DEG_TO_RAD;
  const tiltRad = tiltDeg * DEG_TO_RAD;

  const x = Math.cos(angleRad) * Math.cos(tiltRad) * distance;
  const y = Math.sin(tiltRad) * distance;
  const z = Math.sin(angleRad) * Math.cos(tiltRad) * distance;

  return { position: [x, y, z], target: [0, 0, 0] };
}

// ----------------------------------------------------------------------
// Utilidades adicionales
// ----------------------------------------------------------------------

/**
 * Convierte grados decimales a radianes.
 */
export function degToRad(deg: number): number {
  return deg * DEG_TO_RAD;
}

/**
 * Convierte radianes a grados decimales.
 */
export function radToDeg(rad: number): number {
  return rad * RAD_TO_DEG;
}

/**
 * Interpola linealmente entre dos puntos geográficos.
 * Útil para generar puntos intermedios en calles curvas.
 */
export function interpolateLatLng(
  [lat1, lng1]: LatLng,
  [lat2, lng2]: LatLng,
  t: number
): LatLng {
  return [lat1 + (lat2 - lat1) * t, lng1 + (lng2 - lng1) * t];
}

/**
 * Simplifica una línea geográfica usando el algoritmo de Douglas-Peucker.
 * (Implementación básica para reducir número de puntos)
 */
export function simplifyLatLngLine(
  coords: LatLng[],
  toleranceMeters: number
): LatLng[] {
  if (coords.length <= 2) return coords;

  // Convertir a puntos locales para simplificar en metros
  const converter = new GeoConverter(coords[0][0], coords[0][1]);
  const localPoints = converter.latLngArrayToLocal(coords);
  const simplifiedLocal = douglasPeucker(localPoints, toleranceMeters);
  return converter.localArrayToLatLng(simplifiedLocal);
}

// Algoritmo Douglas-Peucker para puntos 2D {x,z}
function douglasPeucker(points: LocalPoint[], epsilon: number): LocalPoint[] {
  if (points.length <= 2) return points;

  let maxDist = 0;
  let index = 0;
  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last);
    if (dist > maxDist) {
      maxDist = dist;
      index = i;
    }
  }

  if (maxDist > epsilon) {
    const left = points.slice(0, index + 1);
    const right = points.slice(index);
    const leftResult = douglasPeucker(left, epsilon);
    const rightResult = douglasPeucker(right, epsilon);
    return [...leftResult.slice(0, -1), ...rightResult];
  } else {
    return [first, last];
  }
}

function perpendicularDistance(
  point: LocalPoint,
  lineStart: LocalPoint,
  lineEnd: LocalPoint
): number {
  const dx = lineEnd.x - lineStart.x;
  const dz = lineEnd.z - lineStart.z;
  const lengthSq = dx * dx + dz * dz;
  if (lengthSq === 0) return Math.hypot(point.x - lineStart.x, point.z - lineStart.z);

  const t =
    ((point.x - lineStart.x) * dx + (point.z - lineStart.z) * dz) / lengthSq;
  const clampedT = Math.max(0, Math.min(1, t));
  const projX = lineStart.x + clampedT * dx;
  const projZ = lineStart.z + clampedT * dz;
  return Math.hypot(point.x - projX, point.z - projZ);
}

// ----------------------------------------------------------------------
// Ejemplo de uso (comentado)
// ----------------------------------------------------------------------

/*
import { GeoConverter } from './geoConverter';

const converter = new GeoConverter(6.2558643, -75.6016472, true);
const local = converter.latLngToLocal(6.256, -75.602);
console.log(local); // { x: -39.26, z: -15.08 } aproximadamente
*/